import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';
import { useIsMobile } from '../utils/useIsMobile';

import useStyles, { RiskProgressBarStyleProps } from './RiskProgressBar.style';

export interface RiskProgressBarProps {
  riskValue: number;
  size?: 'small' | 'big';
  className?: string;
  withRiskInfoLabel?: boolean;
  withRiskValueLabel?: boolean;
  riskLabel?: string;
  tooltipZIndex?: number;
}

const MAX_RISK_VALUE = 7;

const RiskProgressBar: React.FC<RiskProgressBarProps> = ({
  riskValue,
  size = 'small',
  className,
  withRiskInfoLabel = false,
  withRiskValueLabel = false,
  riskLabel,
  tooltipZIndex
}) => {
  const [t] = useTranslation('components');
  const isMobile = useIsMobile();
  const c = useStyles({ size } as RiskProgressBarStyleProps);

  const Bar = (
    <div className={c.barWrapper}>
      <div className={c.bar}>
        {Array.from({ length: MAX_RISK_VALUE }, (_, i) => i + 1).map( level => (
          <div
            key={`risk-bar-${level}`}
            className={clsx(c.barItem, level <= riskValue ? c.barItemActive : '')}
          />
        ))}
      </div>
      {withRiskValueLabel && (
        <div>
          <Typography variant="body2" component="span" fontWeight="bold" className={c.value}>{riskValue}</Typography>
          <Typography variant="body2" component="span" className={c.max}>{`/${MAX_RISK_VALUE}`}</Typography>
        </div>
      )}
    </div>
  );

  const hasLabel = withRiskInfoLabel || !!riskLabel;

  return (
    <div className={clsx(c.root, className)}>
      {hasLabel && (
        <div className={c.labelWrapper}>
          {withRiskInfoLabel && (
            <div className={c.riskInfoLabelName}>
              <Typography variant="body2" component="span" className={c.riskLabel}>{`${t('fund-list.risk')}: `}</Typography>
              {riskLabel && (
                <Typography variant="body2" component="span" className={c.riskLabel}>
                  {riskLabel}
                </Typography>
              )}
              <Tooltip
                customZIndex={tooltipZIndex}
                preTitle={isMobile ? t('fund-list.risk') : ''}
                title={t('fund-details-dialog.risk-tooltip')}
              />
            </div>
          )}
          {!withRiskInfoLabel && riskLabel && (
            <Typography variant="body2" component="span" className={c.riskLabel}>
              {riskLabel}
            </Typography>
          )}
        </div>
      )}
      {Bar}
    </div>
  );
};

export { RiskProgressBar };


